/**
 * useProjectSelection Hook
 * Extracted from MultiProjectContext.tsx
 * Handles project selection, visibility toggling, and localStorage persistence
 */

import { useCallback, useEffect, useRef } from 'react';
import { log } from '@/shared/utils/logger';
import type { ProjectDataWithMeta } from '../types/multiProject';

function sameProjectIds(left: Set<string>, right: Set<string>) {
  return left.size === right.size && [...left].every(id => right.has(id));
}

interface UseProjectSelectionParams {
  projectsData: Map<string, ProjectDataWithMeta>;
  visibleProjectIds: Set<string>;
  selectedProjectIds?: string[];
  setSelectedProjectIds: (ids: string[]) => void;
  setVisibleProjectIds: (value: Set<string> | ((prev: Set<string>) => Set<string>)) => void;
  setActiveProjectId: (updater: (prev: string | null) => string | null) => void;
  setError: (error: string | null) => void;
  loadProjectData: (projectId: string, options?: { forceReload?: boolean }) => Promise<ProjectDataWithMeta>;
  selectionScopeKey?: string;
}

export function useProjectSelection({
  projectsData,
  visibleProjectIds,
  selectedProjectIds,
  setSelectedProjectIds,
  setVisibleProjectIds,
  setActiveProjectId,
  setError,
  loadProjectData,
  selectionScopeKey
}: UseProjectSelectionParams) {
  const selectionRequestRef = useRef(0);
  const desiredVisibleProjectIdsRef = useRef(new Set(visibleProjectIds));
  const previousSelectionScopeKeyRef = useRef(selectionScopeKey);
  const previousVisibleProjectIdsRef = useRef(new Set(visibleProjectIds));
  const publishedVisibleProjectIdsRef = useRef<Set<string> | null>(null);
  const latestSelectedProjectIdsRef = useRef(selectedProjectIds);
  const ownSelectedPublicationsRef = useRef(new WeakSet<string[]>());

  useEffect(() => {
    latestSelectedProjectIdsRef.current = selectedProjectIds;
    if (selectedProjectIds && !ownSelectedPublicationsRef.current.has(selectedProjectIds)
      && !sameProjectIds(desiredVisibleProjectIdsRef.current, new Set(selectedProjectIds))) {
      desiredVisibleProjectIdsRef.current = new Set(selectedProjectIds);
      selectionRequestRef.current += 1;
    }
  }, [selectedProjectIds]);

  useEffect(() => {
    const scopeChanged = previousSelectionScopeKeyRef.current !== selectionScopeKey;
    const visibleProjectsChanged = !sameProjectIds(
      previousVisibleProjectIdsRef.current,
      visibleProjectIds,
    );
    const isOwnPublication = publishedVisibleProjectIdsRef.current === visibleProjectIds;
    if (isOwnPublication) publishedVisibleProjectIdsRef.current = null;

    if (!scopeChanged && !visibleProjectsChanged) {
      return;
    }

    previousSelectionScopeKeyRef.current = selectionScopeKey;
    previousVisibleProjectIdsRef.current = new Set(visibleProjectIds);
    // A cached selection can publish immediately before a newer load starts
    // in the same React batch. Observing that earlier publication is not an
    // external catalog/access change and must not cancel the newer request.
    if (!scopeChanged && isOwnPublication) return;
    publishedVisibleProjectIdsRef.current = null;
    selectionRequestRef.current += 1;
    desiredVisibleProjectIdsRef.current = new Set(visibleProjectIds);
  }, [selectionScopeKey, visibleProjectIds]);

  useEffect(() => () => {
    selectionRequestRef.current += 1;
  }, []);

  const selectProjects = useCallback(
    async (projectIds: string[], mode: 'toggle' | 'set' | 'add' = 'set') => {
      const selectionRequest = ++selectionRequestRef.current;
      const selectedIdsAtRequestStart = latestSelectedProjectIdsRef.current;
      // Remove duplicates and invalid IDs from input
      const validProjectIds = projectIds.filter(id => {
        if (!id) return false;
        if (typeof id === 'string' && id.includes(',')) {
          return false;
        }
        return true;
      });

      const uniqueProjectIds = [...new Set(validProjectIds)];
      if (projectIds.length !== uniqueProjectIds.length) {
        log.debug('MultiProject', 'Duplicate or invalid project IDs removed');
      }

      setError(null);

      const currentVisible = new Set(desiredVisibleProjectIdsRef.current);
      let finalVisibleSet = new Set(currentVisible);

      if (mode === 'set') {
        finalVisibleSet = new Set(uniqueProjectIds);
      } else if (mode === 'add') {
        uniqueProjectIds.forEach(id => finalVisibleSet.add(id));
      } else if (mode === 'toggle') {
        uniqueProjectIds.forEach(id => {
          if (finalVisibleSet.has(id)) {
            finalVisibleSet.delete(id);
          } else {
            finalVisibleSet.add(id);
          }
        });
      }

      const finalVisibleIds = Array.from(finalVisibleSet);
      desiredVisibleProjectIdsRef.current = new Set(finalVisibleIds);

      // CRITICAL FIX: Immediately mark that user has preferences when they interact
      try {
        const storedState = localStorage.getItem('unified_project_state');
        const appState = storedState ? JSON.parse(storedState) : {};

        if (!appState.hasUserPreferences) {
          const updatedState = {
            ...appState,
            hasUserPreferences: true
          };
          localStorage.setItem('unified_project_state', JSON.stringify(updatedState));
        }
      } catch (e) {
        log.error('MultiProject', 'Error updating hasUserPreferences:', e);
      }

      // Update selected state immediately so the filter trigger reflects the user's intent,
      // but don't expose newly selected projects to the visible set until their data is ready.
      ownSelectedPublicationsRef.current.add(finalVisibleIds);
      setSelectedProjectIds(finalVisibleIds);
      setActiveProjectId(prev => {
        if (prev && finalVisibleIds.includes(prev)) return prev;
        return finalVisibleIds[0] || null;
      });

      // Load projects that are selected to become visible but are not fully cached yet.
      const projectsToLoad = finalVisibleIds.filter(id => !projectsData.get(id)?.isLoaded);

      if (projectsToLoad.length > 0) {
        log.debug('MultiProject', 'Loading projects from server:', projectsToLoad);
        const loadPromises = projectsToLoad.map(async id => {
          try {
            const data = await loadProjectData(id);
            return data;
          } catch (err) {
            // Deselecting a project or changing accounts cancels its request.
            // This expected control flow must not look like a server failure.
            if (!(typeof err === 'object' && err !== null && 'name' in err && err.name === 'AbortError')) {
              log.error('MultiProject', `Failed to load project ${id}:`, err);
            }
            return null;
          }
        });
        await Promise.all(loadPromises);
      } else {
        log.debug('MultiProject', 'All projects already cached');
      }

      if (selectionRequest !== selectionRequestRef.current) {
        return;
      }
      // Catalog reconciliation may change the selected set while the old
      // visible set stays the same. Do not restore that superseded intent.
      if (projectsToLoad.length > 0 && latestSelectedProjectIdsRef.current
        && latestSelectedProjectIdsRef.current !== selectedIdsAtRequestStart
        && !sameProjectIds(new Set(latestSelectedProjectIdsRef.current), new Set(finalVisibleIds))) {
        return;
      }

      const nextVisibleProjectIds = new Set(finalVisibleIds);
      publishedVisibleProjectIdsRef.current = nextVisibleProjectIds;
      setVisibleProjectIds(nextVisibleProjectIds);

      log.debug('MultiProject', '📊 Final cache size:', projectsData.size, 'projects');
    },
    [
      projectsData,
      setSelectedProjectIds,
      setVisibleProjectIds,
      setActiveProjectId,
      setError,
      loadProjectData
    ]
  );

  return { selectProjects };
}
